import { Head, useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Vendor {
  id: number
  display_name: string
}

interface ContactForm {
  prefix: string
  first_name: string
  last_name: string
  title: string
  address: string
  address2: string
  city: string
  state: string
  zip: string
  phone: string
  cell_phone: string
  email: string
  notes: string
}

interface Props {
  vendor: Vendor
  contact: ContactForm
  errors?: Record<string, string[]>
}

export default function New({ vendor, contact, errors }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Subcontractors", href: "/company/vendors" },
    { title: vendor.display_name, href: `/company/vendors/${vendor.id}` },
    { title: "Contacts", href: `/company/vendors/${vendor.id}/contacts` },
    {
      title: "New Contact",
      href: `/company/vendors/${vendor.id}/contacts/new`,
    },
  ]

  const { data, setData, post, processing, transform } = useForm({
    prefix: contact.prefix || "",
    first_name: contact.first_name || "",
    last_name: contact.last_name || "",
    title: contact.title || "",
    address: contact.address || "",
    address2: contact.address2 || "",
    city: contact.city || "",
    state: contact.state || "",
    zip: contact.zip || "",
    phone: contact.phone || "",
    cell_phone: contact.cell_phone || "",
    email: contact.email || "",
    notes: contact.notes || "",
  })

  transform((data) => ({ vendor_contact: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`/company/vendors/${vendor.id}/contacts`)
  }

  const inputClass =
    "mt-1 block w-full rounded border border-hpi-light-gray px-3 py-2 focus:border-hpi-active-blue focus:outline-none"
  const labelClass = "block text-sm font-medium text-hpi-dark-gray"

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="New Contact" />

      <div className="p-6">
        <h1 className="mb-6 text-2xl font-semibold">New Contact</h1>

        <form onSubmit={handleSubmit} className="max-w-2xl space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Prefix</label>
              <input
                type="text"
                value={data.prefix}
                onChange={(e) => setData("prefix", e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>First Name</label>
              <input
                type="text"
                value={data.first_name}
                onChange={(e) => setData("first_name", e.target.value)}
                className={inputClass}
              />
              {errors?.first_name && (
                <p className="text-hpi-red mt-1 text-sm">
                  {errors.first_name[0]}
                </p>
              )}
            </div>
            <div>
              <label className={labelClass}>Last Name</label>
              <input
                type="text"
                value={data.last_name}
                onChange={(e) => setData("last_name", e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Title</label>
            <input
              type="text"
              value={data.title}
              onChange={(e) => setData("title", e.target.value)}
              className={inputClass}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Phone</label>
              <input
                type="text"
                value={data.phone}
                onChange={(e) => setData("phone", e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Cell Phone</label>
              <input
                type="text"
                value={data.cell_phone}
                onChange={(e) => setData("cell_phone", e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Email</label>
            <input
              type="email"
              value={data.email}
              onChange={(e) => setData("email", e.target.value)}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Address</label>
            <input
              type="text"
              value={data.address}
              onChange={(e) => setData("address", e.target.value)}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Address 2</label>
            <input
              type="text"
              value={data.address2}
              onChange={(e) => setData("address2", e.target.value)}
              className={inputClass}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>City</label>
              <input
                type="text"
                value={data.city}
                onChange={(e) => setData("city", e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>State</label>
              <input
                type="text"
                value={data.state}
                onChange={(e) => setData("state", e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Zip</label>
              <input
                type="text"
                value={data.zip}
                onChange={(e) => setData("zip", e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Notes</label>
            <textarea
              value={data.notes}
              onChange={(e) => setData("notes", e.target.value)}
              rows={4}
              className={inputClass}
            />
          </div>

          <div className="flex justify-end space-x-4">
            <a
              href={`/company/vendors/${vendor.id}/contacts`}
              className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
            >
              Cancel
            </a>
            <button
              type="submit"
              disabled={processing}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
            >
              {processing ? "Saving..." : "Save Contact"}
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
